import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Relatorios.module.css';
import { listar, COLECOES } from '../../services/firestore';
import {
  calcularStatusEstoque,
  posicaoDeveAparecer,
  labelStatus,
  STATUS,
} from '../../utils/estoque';
import { exportarPDF, exportarExcel } from '../../utils/exportar';

// ============================================================
// RELATÓRIO: INVENTÁRIO (snapshot consolidado de estoque)
// ============================================================
// Lista uma linha por posição (produto + estoque), com qtd atual,
// mínimo/máximo cadastrados e status. Útil para conferência física.
// ============================================================

export default function RelInventario() {
  const navigate = useNavigate();

  const [produtos, setProdutos] = useState([]);
  const [posicoes, setPosicoes] = useState([]);
  const [estoques, setEstoques] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [filtroEstoque, setFiltroEstoque] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [prods, pos, ests] = await Promise.all([
        listar(COLECOES.PRODUTOS, { ordenarPor: 'nome' }),
        listar(COLECOES.ESTOQUE_POSICAO),
        listar(COLECOES.ESTOQUES, { ordenarPor: 'nome' }),
      ]);
      setProdutos(prods.filter((p) => p.ativo !== false));
      setPosicoes(pos);
      setEstoques(ests.filter((e) => e.ativo !== false));
    } catch (e) {
      setErro(e.message || 'Erro ao carregar dados.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const linhas = useMemo(() => {
    const mapProdutos = {};
    produtos.forEach((p) => {
      mapProdutos[p.id] = p;
    });

    const todasLinhas = [];
    posicoes.forEach((pos) => {
      const prod = mapProdutos[pos.produtoId];
      if (!prod) return;

      const estoqueDoc = estoques.find((e) => e.id === pos.estoqueId);
      const nivel = (estoqueDoc?.niveis || []).find(
        (n) => n.produtoId === pos.produtoId
      );
      const min = Number(nivel?.minimo) || 0;
      const max = Number(nivel?.maximo) || 0;
      const qtd = Number(pos.qtdReal) || 0;

      if (!posicaoDeveAparecer(qtd, min)) return;

      const statusKey = calcularStatusEstoque(qtd, min, max);

      todasLinhas.push({
        cor: prod.cor || '',
        resina: prod.resina || '',
        estoque: pos.estoqueNome || estoqueDoc?.nome || '(sem nome)',
        estoqueId: pos.estoqueId,
        qtd,
        minimo: min,
        maximo: max,
        statusKey,
        statusLabel: labelStatus(statusKey),
      });
    });

    return todasLinhas.sort((a, b) => {
      const e = a.estoque.localeCompare(b.estoque);
      if (e !== 0) return e;
      return a.cor.localeCompare(b.cor);
    });
  }, [produtos, posicoes, estoques]);

  const linhasFiltradas = useMemo(() => {
    return linhas.filter((l) => {
      if (filtroEstoque && l.estoqueId !== filtroEstoque) return false;
      if (filtroStatus && l.statusKey !== filtroStatus) return false;
      if (busca) {
        const q = busca.toLowerCase();
        const match =
          l.cor.toLowerCase().includes(q) ||
          l.resina.toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [linhas, filtroEstoque, filtroStatus, busca]);

  const resumo = useMemo(() => {
    const ok = linhasFiltradas.filter((l) => l.statusKey === STATUS.OK).length;
    const criticos = linhasFiltradas.filter(
      (l) => l.statusKey === STATUS.CRITICO || l.statusKey === STATUS.ZERADO
    ).length;
    const atencao = linhasFiltradas.filter((l) => l.statusKey === STATUS.ATENCAO).length;
    return { total: linhasFiltradas.length, ok, criticos, atencao };
  }, [linhasFiltradas]);

  const colunasExport = [
    { header: 'Cor', dataKey: 'cor' },
    { header: 'Resina', dataKey: 'resina' },
    { header: 'Estoque', dataKey: 'estoque' },
    { header: 'Qtd (L)', dataKey: 'qtd' },
    { header: 'Mín', dataKey: 'minimo' },
    { header: 'Máx', dataKey: 'maximo' },
    { header: 'Status', dataKey: 'statusLabel' },
  ];

  function handlePDF() {
    const sub = [
      filtroEstoque
        ? `Estoque: ${estoques.find((e) => e.id === filtroEstoque)?.nome}`
        : '',
      filtroStatus ? `Status: ${labelStatus(filtroStatus)}` : '',
    ].filter(Boolean).join(' · ');
    exportarPDF({
      titulo: 'Inventário de Estoque',
      subtitulo: sub,
      colunas: colunasExport,
      dados: linhasFiltradas,
      orientacao: 'landscape',
    });
  }

  function handleExcel() {
    exportarExcel({
      titulo: 'Inventário de Estoque',
      colunas: colunasExport,
      dados: linhasFiltradas,
      nomeAba: 'Inventário',
    });
  }

  return (
    <>
      <div className={styles.topbar}>
        <div>
          <div className={styles.pageTitle}>Inventário (snapshot)</div>
          <div className={styles.pageSub}>
            Estoque atual consolidado por produto e por estoque.
          </div>
        </div>
        <button
          className={styles.btnVoltar}
          onClick={() => navigate('/relatorios')}
        >
          ← Voltar para Relatórios
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '14px 0',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.resumo} style={{ marginTop: 14 }}>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Total de posições</div>
          <div className={styles.resumoValor}>{resumo.total}</div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Em OK</div>
          <div className={`${styles.resumoValor} ${styles.ok}`}>{resumo.ok}</div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Em atenção</div>
          <div className={`${styles.resumoValor} ${styles.info}`}>
            {resumo.atencao}
          </div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Crítico / Zerado</div>
          <div className={`${styles.resumoValor} ${styles.warn}`}>
            {resumo.criticos}
          </div>
        </div>
      </div>

      <div className={styles.filtros}>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Buscar</label>
          <input
            className={styles.filtroInput}
            type="text"
            placeholder="Produto, cor, resina..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Estoque</label>
          <select
            className={styles.filtroSelect}
            value={filtroEstoque}
            onChange={(e) => setFiltroEstoque(e.target.value)}
          >
            <option value="">Todos</option>
            {estoques.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Status</label>
          <select
            className={styles.filtroSelect}
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value={STATUS.OK}>OK</option>
            <option value={STATUS.ATENCAO}>Em atenção</option>
            <option value={STATUS.CRITICO}>Abaixo do mín</option>
            <option value={STATUS.ZERADO}>Zerado</option>
            <option value={STATUS.ACIMA_MAX}>Acima do máx</option>
          </select>
        </div>

        <div className={styles.btnExportar}>
          <button
            className={styles.btnExp}
            onClick={handlePDF}
            disabled={linhasFiltradas.length === 0}
          >
            📄 PDF
          </button>
          <button
            className={styles.btnExp}
            onClick={handleExcel}
            disabled={linhasFiltradas.length === 0}
          >
            📊 Excel
          </button>
        </div>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {linhasFiltradas.length} de {linhas.length} posições
          </span>
        </div>
        <div className={styles.tabelaScroll}>
          <table className={styles.tabela}>
            <thead>
              <tr>
                <th>Cor</th>
                <th>Resina</th>
                <th>Estoque</th>
                <th className={styles.textRight}>Qtd (L)</th>
                <th className={styles.textRight}>Mín</th>
                <th className={styles.textRight}>Máx</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={7} className={styles.semResultados}>
                    Carregando...
                  </td>
                </tr>
              ) : linhasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.semResultados}>
                    Nenhuma posição encontrada com os filtros aplicados.
                  </td>
                </tr>
              ) : (
                linhasFiltradas.map((l, idx) => (
                  <tr key={idx}>
                    <td><strong>{l.cor || '—'}</strong></td>
                    <td>{l.resina || '—'}</td>
                    <td>{l.estoque}</td>
                    <td
                      className={`${styles.textRight} ${
                        l.statusKey === STATUS.CRITICO || l.statusKey === STATUS.ZERADO
                          ? styles.warn
                          : ''
                      }`}
                    >
                      <strong>{l.qtd}</strong>
                    </td>
                    <td className={`${styles.textRight} ${styles.muted}`}>
                      {l.minimo || '—'}
                    </td>
                    <td className={`${styles.textRight} ${styles.muted}`}>
                      {l.maximo || '—'}
                    </td>
                    <td>
                      <span
                        className={`${styles.badge} ${
                          l.statusKey === STATUS.OK
                            ? styles.badgeOk
                            : l.statusKey === STATUS.ATENCAO
                            ? styles.badgeAtencao
                            : l.statusKey === STATUS.ACIMA_MAX
                            ? styles.badgeInfo
                            : styles.badgeWarn
                        }`}
                      >
                        {l.statusLabel}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
